import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useRenewTokenMutation } from "../../../app/services/auth_api";
import { useRefreshableTimeout } from "../../../lib/use_refreshable_timeout";
import { anonymize } from "../../analytics";
import { getItem } from "../../local_storage/utils";
import { setToken } from "../actions";
import { selectJwtExpiration, selectJwtIsExpired, selectToken } from "../selectors";

// Refresh the token 5m before it expires
const REFRESH_TIME_BUFFER = 5 * 60 * 1000;

export const useKeepTokenFresh = () => {
  const dispatch = useAppDispatch();
  const [renew] = useRenewTokenMutation();
  const currentToken = useAppSelector(selectToken);
  const expiration = useAppSelector(selectJwtExpiration);
  const isExpired = useAppSelector(selectJwtIsExpired);

  // Sync token from localStorage when tab gains focus
  // This ensures we pick up token refreshes from other tabs
  useEffect(() => {
    const handleFocus = () => {
      console.info("🔒 Tab gained focus, syncing token from localStorage");
      const tokenInStorage = getItem<string>("token") ?? "";
      if (tokenInStorage && tokenInStorage !== currentToken) {
        dispatch(setToken(tokenInStorage));
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [dispatch, currentToken]);

  const setRefreshTime = useRefreshableTimeout(async () => {
    const { token } = await renew().unwrap();
    if (!token) {
      Sentry.captureException(new Error("UseKeepTokenFresh hook: Token renewal failed"));
      console.warn("Token renewal failed");
      // Return early to avoid setting the token to null or empty string. If a
      // refresh fails, we can continue trying to use the old token until we're
      // forced to log out. This is in case the refresh fails due to a network
      // error or something else that might be temporary.
      return;
    }
    dispatch(setToken(token));
  });

  useEffect(() => {
    if (expiration) {
      if (isExpired) {
        // Token appears expired based on our Redux state
        // Check if localStorage has a fresher token (e.g., from another tab)
        const tokenInStorage = getItem<string>("token") ?? "";
        if (tokenInStorage && tokenInStorage !== currentToken) {
          // Found a different token in localStorage - sync it and let selectors recompute
          console.info("🔒 Expired token detected, found different token in localStorage - syncing");
          dispatch(setToken(tokenInStorage));
          // The effect will re-run with the new expiration from the updated token
        } else {
          // No token in storage, or same expired token - clear and log out
          console.info("🔒 Expired token, clearing token");
          dispatch(setToken(""));
          anonymize();
        }
      } else {
        const refreshIn = Math.max(0, expiration.getTime() - Date.now() - REFRESH_TIME_BUFFER);
        setRefreshTime(refreshIn);

        console.info(`🔒 Token refresh scheduled for ${new Date(Date.now() + refreshIn).toLocaleString()}`);
      }
    }
  }, [dispatch, expiration, isExpired, setRefreshTime, currentToken]);
};
